import type { Lead, Application } from '../../lib/api'
import { SurfaceCard } from '../ui'
import { formatStage } from '../../lib/format'
import {
  ActivationContext,
  ClientReturnedContext,
  EsignCompletedContext,
  EsignSentContext,
  LiveContext,
  NewBusinessContext,
  PendedContext,
  ProcessorSubmissionContext,
  ReadyToBuildContext,
  UnderwritingContext,
  VerificationContext,
} from './stage-contexts'

/**
 * Stage-aware context card — shows the most relevant information
 * and CTAs for the lead's current pipeline stage. Renders as the
 * FIRST card in the detail view, above the static business info.
 *
 * Each stage gets a dedicated layout so ops never has to hunt for
 * what matters right now. The card is intentionally large and
 * visually dominant.
 */
export function StageContextCard({
  lead,
  application,
  onNavigate,
}: {
  lead: Lead
  application?: Application | null
  onNavigate: (path: string) => void
}) {
  const stage = lead.stage
  const app = application ?? lead.application

  return (
    <SurfaceCard padding="p-0">
        {stage === 'new_business' && (
          <NewBusinessContext lead={lead} app={app} onNavigate={onNavigate} />
        )}
        {stage === 'esign_sent' && (
          <EsignSentContext app={app} />
        )}
        {stage === 'esign_completed' && (
          <EsignCompletedContext app={app} />
        )}
        {stage === 'verification' && app && (
          <VerificationContext app={app} />
        )}
        {stage === 'processor_submission' && (
          <ProcessorSubmissionContext app={app} />
        )}
        {stage === 'underwriting_review' && (
          <UnderwritingContext app={app} />
        )}
        {stage === 'client_returned' && (
          <ClientReturnedContext app={app} />
        )}
        {(stage === 'conditional_approval' || stage === 'pended') && (
          <PendedContext lead={lead} app={app} />
        )}
        {stage === 'ready_to_build' && (
          <ReadyToBuildContext lead={lead} onNavigate={onNavigate} />
        )}
        {stage === 'activation' && (
          <ActivationContext lead={lead} />
        )}
        {stage === 'live' && (
          <LiveContext lead={lead} />
        )}
        {(stage === 'declined_by_agms' || stage === 'declined_by_fiserv') && (
          <div className="border-l-4 border-red-500 bg-red-50 p-6">
            <h3 className="text-lg font-semibold text-red-900">{formatStage(stage)}</h3>
            <p className="mt-2 text-sm text-red-800">
              This lead has been closed out as declined. No automated onboarding action is pending.
            </p>
          </div>
        )}
    </SurfaceCard>
  )
}
